import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useErrand } from '../contexts/ErrandContext';
import { useNotifications } from '../contexts/NotificationContext';
import { DashboardStatsSkeleton } from '../components/UI/LoadingSkeleton';
import EmptyState from '../components/UI/EmptyState';

interface QuickStats {
  pendingErrands: number;
  activeErrands: number;
  completedErrands: number;
  totalSpent: number;
  totalEarnings?: number;
  availableErrands?: number;
}

interface ActivityItem {
  id: string;
  type: 'errand_created' | 'errand_accepted' | 'errand_completed' | 'payment_received' | 'new_errand_available';
  title: string;
  description: string;
  timestamp: string;
  icon: string;
  link?: string;
}

const DashHomePage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { userErrands, fetchUserErrands, availableErrands, fetchAvailableErrands, driverErrands, fetchDriverErrands } = useErrand();
  const { unreadCount } = useNotifications();
  const [quickStats, setQuickStats] = useState<QuickStats>({
    pendingErrands: 0,
    activeErrands: 0,
    completedErrands: 0,
    totalSpent: 0
  });
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadHomeData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadHomeData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Fetch user errands
      await fetchUserErrands();

      // Fetch available errands for drivers/couriers
      if (user.role === 'driver' || user.isDriver || user.role === 'courier') {
        await fetchAvailableErrands();
        await fetchDriverErrands();
      }
    } catch (error) {
      console.error('Error loading home data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && userErrands) {
      // Calculate quick stats
      const pending = userErrands.filter(e => e.status === 'pending').length;
      const active = userErrands.filter(e => ['accepted', 'in_progress', 'picked_up'].includes(e.status)).length;
      const completed = userErrands.filter(e => e.status === 'completed').length;
      const spent = userErrands
        .filter(e => e.payment_status === 'completed')
        .reduce((sum, e) => sum + (e.total_cost || 0), 0);

      // Calculate earnings for drivers/couriers
      let earnings = 0;
      if (user.role === 'driver' || user.isDriver || user.role === 'courier') {
        earnings = driverErrands
          .filter(e => e.payment_status === 'completed')
          .reduce((sum, e) => sum + (e.driver_earnings || 0), 0);
      }

      setQuickStats({
        pendingErrands: pending,
        activeErrands: active,
        completedErrands: completed,
        totalSpent: spent,
        totalEarnings: earnings,
        availableErrands: availableErrands.length
      });

      // Generate recent activity
      const activities: ActivityItem[] = [];

      // Recent errands
      userErrands.slice(0, 5).forEach(errand => {
        activities.push({
          id: errand.id,
          type: 'errand_created',
          title: errand.title,
          description: `Status: ${errand.status.replace('_', ' ')}`,
          timestamp: errand.created_at,
          icon: '📦',
          link: `/errands`
        });
      });

      // Sort by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentActivity(activities.slice(0, 5));
    }
  }, [user, userErrands, driverErrands, availableErrands]);

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero Skeleton */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
            <div className="animate-pulse">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-gray-200 rounded-2xl"></div>
                <div className="space-y-2">
                  <div className="h-8 bg-gray-200 rounded w-48"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
              <div className="space-y-4 text-center">
                <div className="h-6 bg-gray-200 rounded w-64 mx-auto"></div>
                <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
                <div className="flex justify-center space-x-4 mt-8">
                  <div className="h-12 bg-gray-200 rounded-xl w-48"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user ? (
          <>
            {/* Enhanced Hero Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12 mb-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-4 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="text-4xl text-white">⚡</span>
                  </div>
                  <div>
                    <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      NkeleGo Dash
                    </h1>
                    <p className="text-gray-600 font-medium text-lg">Lightning-fast errand services</p>
                  </div>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                  Welcome back, {user.name || user.email?.split('@')[0]}! 👋
                </h2>
                <p className="text-gray-600 text-lg mb-8">Ready to get things done? Let's make it happen.</p>

                {/* Quick Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/errand/request"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <span className="text-xl mr-2">🚀</span>
                    Request New Errand
                  </Link>
                  {(user.role === 'driver' || user.isDriver || user.role === 'courier') && (
                    <Link
                      to="/driver/errands"
                      className="inline-flex items-center px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-50 transform hover:scale-105 transition-all duration-200"
                    >
                      <span className="text-xl mr-2">💼</span>
                      View Available Jobs
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {user.role === 'driver' || user.isDriver || user.role === 'courier' ? (
                <>
                  <Link
                    to="/driver/errands"
                    className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-600 mb-1">Available Jobs</p>
                        <p className="text-3xl font-bold text-blue-600 group-hover:scale-110 transition-transform">
                          {quickStats.availableErrands || 0}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Ready to accept</p>
                      </div>
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl text-white">📋</span>
                      </div>
                    </div>
                  </Link>
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-600 mb-1">Total Earnings</p>
                        <p className="text-3xl font-bold text-green-600">
                          P{quickStats.totalEarnings?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">This month</p>
                      </div>
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl text-white">💰</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/errands"
                    className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-600 mb-1">Active Errands</p>
                        <p className="text-3xl font-bold text-purple-600 group-hover:scale-110 transition-transform">
                          {quickStats.activeErrands}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">In progress</p>
                      </div>
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl text-white">🚚</span>
                      </div>
                    </div>
                  </Link>
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-600 mb-1">Total Spent</p>
                        <p className="text-3xl font-bold text-blue-600">
                          P{quickStats.totalSpent?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">This month</p>
                      </div>
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl text-white">💳</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Link
                to="/errands"
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-200 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Completed</p>
                    <p className="text-3xl font-bold text-green-600 group-hover:scale-110 transition-transform">
                      {quickStats.completedErrands}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Successfully delivered</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-2xl text-white">✅</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/chat"
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl hover:scale-105 transition-all duration-200 group relative"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Messages</p>
                    <p className="text-3xl font-bold text-indigo-600 group-hover:scale-110 transition-transform">
                      {unreadCount > 0 ? unreadCount : '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Unread messages</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-2xl text-white">💬</span>
                  </div>
                </div>
                {unreadCount > 0 && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </div>
                )}
              </Link>
            </div>

            {/* Essential Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {!(user.role === 'driver' || user.isDriver || user.role === 'courier') && (
                <Link
                  to="/errand/request"
                  className="bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transform hover:scale-105 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                      <span className="text-2xl">🚀</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Request Errand</h3>
                      <p className="text-sm text-blue-100">Get it delivered fast</p>
                    </div>
                  </div>
                </Link>
              )}

              <Link
                to="/errands"
                className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 hover:shadow-xl hover:border-blue-300 transform hover:scale-105 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <span className="text-2xl">📦</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">My Errands</h3>
                    <p className="text-sm text-gray-600">Track your orders</p>
                  </div>
                </div>
              </Link>

              {(user.role === 'driver' || user.isDriver || user.role === 'courier') && (
                <Link
                  to="/driver/errands"
                  className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 hover:shadow-xl hover:border-green-300 transform hover:scale-105 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <span className="text-2xl">💼</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">Available Jobs</h3>
                      <p className="text-sm text-gray-600">Start earning now</p>
                    </div>
                  </div>
                </Link>
              )}

              <Link
                to="/payments"
                className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 hover:shadow-xl hover:border-purple-300 transform hover:scale-105 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <span className="text-2xl">💳</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Payments</h3>
                    <p className="text-sm text-gray-600">View transactions</p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
                <Link
                  to="/errands"
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  View All
                </Link>
              </div>
              {recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.map((activity) => (
                    <Link
                      key={activity.id}
                      to={activity.link || '#'}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-2xl">{activity.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(activity.timestamp).toLocaleString('en-BW', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="📋"
                  title="No Recent Activity"
                  message="Your recent errand activity will appear here"
                  actionLabel="Request Errand"
                  actionPath="/errand/request"
                />
              )}
            </div>
          </>
        ) : (
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-green-600 mb-2">Dash</h1>
              <p className="text-gray-600">Quick errand services</p>
            </div>
            <div className="space-y-4">
              <Link
                to="/auth/login"
                className="block w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/auth/signup"
                className="block w-full bg-green-100 text-green-700 py-3 px-6 rounded-lg font-semibold hover:bg-green-200 transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashHomePage;
